/* Installable online radio: no stream caching and no automatic audio-interrupting reload. */
(() => {
  'use strict';
  if (window.__asifRadioPwa) return;
  window.__asifRadioPwa = true;
  const policy = window.AsIfRadioInstallPolicy;
  if (!policy) return;
  const KEY = 'asif.radio.install.v1';
  const SESSION = 'asif.radio.install.session';
  const modes = ['standalone', 'fullscreen', 'minimal-ui'].map(mode => matchMedia(`(display-mode: ${mode})`));
  const standalone = () => navigator.standalone === true || modes.some(mode => mode.matches);
  const apple = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const android = /Android/.test(navigator.userAgent);
  const preview = () => /(?:preview_theme_id|_ab)=/.test(location.search) || window.Shopify?.designMode || window.Shopify?.theme?.role === 'unpublished';
  let storage = true, sessionShown = false, saved;
  try { saved = policy.clean(JSON.parse(localStorage.getItem(KEY) || '{}')); sessionShown = sessionStorage.getItem(SESSION) === '1'; }
  catch { saved = policy.clean(); storage = false; }
  // Persistent installation memory is not evidence after an uninstall.
  if (!standalone()) saved.installed = false;
  let uiGeneration = 0, installGeneration = 0;
  let root, dialog, opener, promptEvent, relatedInstalled = false, listening = 0, lastTick = Date.now();
  let updateAvailable = false, updateDismissed = false, checking = false, lastCheck = 0;
  const release = document.querySelector('meta[name="asif-app-release"]')?.content;
  const save = () => { try { localStorage.setItem(KEY, JSON.stringify(saved)); } catch { storage = false; } };
  const radio = () => !!root?.isConnected && !!document.querySelector('[data-radio-page]');
  const installed = () => standalone() || relatedInstalled;
  const hide = selector => { const el = root?.querySelector(selector); if (el) el.hidden = true; if (selector === '[data-app-invite]') window.AsIfNudge?.release('app-einladung'); };
  const copy = () => { try { return JSON.parse(root?.querySelector('[data-app-copy]')?.textContent || '{}'); } catch { return {}; } };
  // as.if.club (25.09.2026): Hinweis-Verkehr. Laeuft gerade eine andere Einblendung
  // (Club-Hinweis, Einladung), wartet die App-Einladung.
  const nudge = () => window.AsIfNudge;
  const busy = () => !!document.querySelector('dialog[open], .hub.is-consent-blocked, .asif-club-toast')
    || !!(nudge() && nudge().active() && nudge().active() !== 'app-einladung')
    || ['shopify-pc__banner','shopify-pc__prefs'].some(id => { const el = document.getElementById(id); return el && !el.hidden && el.getClientRects().length > 0; })
    || /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName);
  const audioActive = () => ['playing','buffering','connecting','retrying','offline'].includes(document.documentElement.dataset.asifRadioState)
    || !!document.querySelector('[data-radio-recording][data-playback="playing"], [data-radio-recording][data-playback="starting"]');
  const sync = () => {
    document.querySelectorAll('[data-app-open]').forEach(button => { button.hidden = !radio() || installed(); });
    if (installed() || saved.never || !radio() || document.hidden || busy()) hide('[data-app-invite]');
    const install = root?.querySelector('[data-app-install]');
    if (install) install.hidden = !promptEvent || preview() || installed();
    const update = root?.querySelector('[data-app-update]');
    if (update) update.hidden = !updateAvailable || updateDismissed || !standalone() || document.hidden || !!busy();
  };
  const markInstalled = () => { installGeneration += 1; relatedInstalled = true; saved.installed = true; save(); hide('[data-app-invite]'); dialog?.close(); sync(); };
  const dismiss = () => { hide('[data-app-invite]'); nudge()?.release('app-einladung'); sessionShown = true; try { sessionStorage.setItem(SESSION, '1'); } catch {} };
  const open = button => {
    if (!radio() || installed()) return;
    uiGeneration += 1;
    opener = button;
    dismiss();
    root.querySelectorAll('[data-app-help]').forEach(help => { help.hidden = help.dataset.appHelp !== (apple ? 'ios' : android ? 'android' : 'desktop'); });
    root.querySelector('[data-app-preview]').hidden = !preview();
    root.querySelector('[data-app-message]').textContent = '';
    sync();
    dialog.showModal();
  };
  const checkUpdate = async () => {
    if (!radio() || !standalone() || preview() || document.hidden || checking || Date.now() - lastCheck < 900000 || !release) return;
    checking = true; lastCheck = Date.now();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch('/pages/radio?source=pwa-update', { cache:'no-store', credentials:'same-origin', signal:controller.signal });
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = doc.querySelector('meta[name="asif-app-release"]')?.content;
      if (next && next !== release) { updateAvailable = true; sync(); }
    } catch { /* Offline listening/recovery belongs to the audio controller. */ }
    finally { clearTimeout(timer); checking = false; }
  };
  const related = async () => {
    if (!navigator.getInstalledRelatedApps || standalone() || !radio()) return;
    const generation = ++installGeneration, requestRoot = root;
    try {
      const apps = await navigator.getInstalledRelatedApps();
      if (generation !== installGeneration || root !== requestRoot || !radio()) return;
      relatedInstalled = apps.some(app => {
        if (app.platform !== 'webapp') return false;
        if (app.id) return app.id === 'https://www.as-if-records.com/pages/radio';
        // Android can report only the manifest URL. Accept our own manifest only.
        try {
          const url = new URL(app.url);
          return url.origin === 'https://www.as-if-records.com'
            && /^\/cdn\/shop\/t\/\d+\/assets\/asif-radio-manifest\.json$/.test(url.pathname);
        } catch { return false; }
      });
      saved.installed = relatedInstalled; save();
      if (relatedInstalled) { promptEvent = null; hide('[data-app-invite]'); dialog?.close(); }
      sync();
    } catch { /* Unavailable detection must leave manual installation help reachable. */ }
  };
  const mount = () => {
    if (!promptEvent && window.__asifBip && !installed()) promptEvent = window.__asifBip;
    root = document.querySelector('[data-radio-pwa]');
    dialog = root?.querySelector('[data-app-dialog]');
    if (!root) { sync(); return; }
    if (!root.dataset.mounted) {
      root.dataset.mounted = 'true';
      dialog.addEventListener('close', () => { uiGeneration += 1; if (opener?.isConnected && !opener.hidden) opener.focus({preventScroll:true}); sync(); });
    }
    if (standalone()) { saved.installed = true; save(); }
    sync(); related(); checkUpdate();
  };
  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-app-open], [data-app-close], [data-app-dismiss], [data-app-never], [data-app-installed], [data-app-install], [data-app-reload], [data-app-update-dismiss]');
    if (!button || !radio()) return;
    if (button.hasAttribute('data-app-open')) { open(button); return; }
    if (button.hasAttribute('data-app-close')) { dialog.close(); return; }
    if (button.hasAttribute('data-app-dismiss')) { dismiss(); return; }
    if (button.hasAttribute('data-app-never')) { saved.never = true; save(); dialog.close(); return; }
    if (button.hasAttribute('data-app-installed')) { markInstalled(); return; }
    if (button.hasAttribute('data-app-update-dismiss')) { updateDismissed = true; sync(); return; }
    if (button.hasAttribute('data-app-reload')) {
      if (!audioActive() || window.confirm(copy().reload)) location.reload();
      return;
    }
    if (!promptEvent || preview()) return;
    const requestRoot = root, requestGeneration = uiGeneration;
    const request = promptEvent; promptEvent = null; window.__asifBip = null; sync();
    try {
      await request.prompt();
      const choice = await request.userChoice;
      if (!radio() || root !== requestRoot || uiGeneration !== requestGeneration) return;
      root.querySelector('[data-app-message]').textContent = choice.outcome === 'accepted' ? copy().accepted : copy().dismissed;
      // appinstalled, not acceptance alone, confirms installation.
    } catch { if (radio() && root === requestRoot && uiGeneration === requestGeneration) root.querySelector('[data-app-message]').textContent = copy().dismissed; }
  });
  window.addEventListener('beforeinstallprompt', event => {
    // Keep browser's own behavior on other pages; our promotion is radio-only.
    // Audit 24.09.: Das Ereignis kommt nur einmal pro Seitenaufruf. Wer auf der
    // Startseite einsteigt und weich ins Radio wechselt, braucht es dort noch.
    window.__asifBip = event;
    if (!radio()) return;
    event.preventDefault();
    installGeneration += 1;
    relatedInstalled = false; saved.installed = false; save();
    promptEvent = event; sync();
  });
  window.addEventListener('appinstalled', () => { promptEvent = null; window.__asifBip = null; markInstalled(); });
  modes.forEach(mode => mode.addEventListener?.('change', () => { if (standalone()) markInstalled(); else { relatedInstalled = false; saved.installed = false; sync(); related(); } }));
  window.addEventListener('storage', event => {
    if (event.key !== KEY) return;
    try { saved = policy.clean(JSON.parse(event.newValue || '{}')); saved.installed = installed(); hide('[data-app-invite]'); sync(); } catch {}
  });
  document.addEventListener('asif:page:unload', () => { dialog?.close(); hide('[data-app-invite]'); hide('[data-app-update]'); root = null; lastTick = Date.now(); });
  document.addEventListener('asif:page', mount);
  document.addEventListener('shopify:section:load', mount);
  window.addEventListener('pageshow', mount);
  document.addEventListener('visibilitychange', () => { lastTick = Date.now(); sync(); if (!document.hidden) { related(); checkUpdate(); } });
  setInterval(() => {
    const now = Date.now(), elapsed = Math.min(5000, Math.max(0, now - lastTick)); lastTick = now;
    if (!radio() || document.hidden) return;
    sync();
    const playing = document.documentElement.dataset.asifRadioState === 'playing'
      || !!document.querySelector('[data-radio-recording][data-playback="playing"]');
    if (playing) {
      listening += elapsed;
      const day = new Date().toLocaleDateString('sv-SE');
      if (!saved.days.includes(day)) { saved.days.push(day); saved.days = saved.days.slice(-30); save(); }
    }
    if (policy.eligible({ saved, listening, radio:radio(), visible:!document.hidden, installed:installed(), supported:!!promptEvent || apple || android, blocked:!!busy() || preview(), sessionShown, storage })
      && (!nudge() || nudge().claim('app-einladung', 'marketing'))) {
      saved.count += 1; saved.last = now; saved.daysAtLast = saved.days.length; save();
      sessionShown = true; try { sessionStorage.setItem(SESSION,'1'); } catch { storage = false; }
      const invite = root.querySelector('[data-app-invite]'); if (invite) invite.hidden = false;
    }
    checkUpdate();
  }, 5000);
  mount();
})();
